/**
 * Retention and Purge Policies for postgres.do
 *
 * Implements comprehensive data retention management with support for:
 * - Time-based retention (e.g., keep last 30 days)
 * - Size-based retention (e.g., keep under 1GB)
 * - Count-based retention (e.g., keep last 1000 records)
 * - Custom policies with complex predicates
 *
 * Compliance features:
 * - GDPR right-to-erasure support
 * - Legal hold capability
 * - Audit trail for all deletions
 * - Cascade policies across related data
 *
 * @module retention
 */

/**
 * Types of retention policies
 */
export type RetentionType = 'time' | 'size' | 'count' | 'custom'

/**
 * Time units for time-based retention
 */
export type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'

/**
 * Size units for size-based retention
 */
export type SizeUnit = 'bytes' | 'kb' | 'mb' | 'gb' | 'tb'

/**
 * Purge action types
 */
export type PurgeAction = 'soft_delete' | 'hard_delete' | 'archive' | 'cryptographic_erasure'

/**
 * Status of a retention policy
 */
export type PolicyStatus = 'active' | 'paused' | 'disabled'

/**
 * Compliance framework identifiers
 */
export type ComplianceFramework = 'gdpr' | 'hipaa' | 'sox' | 'pci_dss' | 'ccpa' | 'custom'

/**
 * Base retention policy interface
 */
export interface RetentionPolicy {
  /** Unique identifier for the policy */
  id: string
  /** Human-readable name */
  name: string
  /** Description of the policy */
  description?: string | undefined
  /** Type of retention policy */
  type: RetentionType
  /** Numeric value for the retention threshold */
  value: number
  /** Unit for the value (time, size, or count) */
  unit?: TimeUnit | SizeUnit | undefined
  /** Target table(s) or data type(s) */
  targets: RetentionTarget[]
  /** Current status of the policy */
  status: PolicyStatus
  /** When the policy was created */
  createdAt: Date
  /** When the policy was last updated */
  updatedAt: Date
  /** Priority (lower = higher priority) */
  priority: number
  /** Compliance frameworks this policy supports */
  compliance?: ComplianceFramework[]
  /** Schedule for policy evaluation (cron expression or interval) */
  schedule?: RetentionSchedule
  /** Action to take when policy triggers */
  action: PurgeAction
  /** Whether to cascade to related data */
  cascade?: CascadePolicy
}

/**
 * Target specification for retention policies
 */
export interface RetentionTarget {
  /** Table name or pattern */
  table: string
  /** Schema name (default: public) */
  schema?: string
  /** Column to evaluate for time-based retention */
  timestampColumn?: string
  /** Column to evaluate for size calculation */
  sizeColumn?: string
  /** Additional WHERE clause predicate */
  predicate?: string
}

/**
 * Schedule configuration for automatic policy execution
 */
export interface RetentionSchedule {
  /** Cron expression (e.g., "0 0 * * *" for daily at midnight) */
  cron?: string
  /** Interval in milliseconds */
  intervalMs?: number
  /** Timezone for cron expressions */
  timezone?: string
  /** Whether to run immediately on policy activation */
  runOnActivation?: boolean
}

/**
 * Cascade policy for related data deletion
 */
export interface CascadePolicy {
  /** Enable cascade deletion */
  enabled: boolean
  /** Tables to cascade to (in order) */
  targets?: string[]
  /** Foreign key columns to follow */
  foreignKeys?: string[]
  /** Maximum depth for cascade (default: 10) */
  maxDepth?: number
}

/**
 * Time-based retention policy
 */
export interface TimeRetentionPolicy extends RetentionPolicy {
  type: 'time'
  unit: TimeUnit
  /** Optional grace period before deletion */
  gracePeriod?: {
    value: number
    unit: TimeUnit
  }
}

/**
 * Size-based retention policy
 */
export interface SizeRetentionPolicy extends RetentionPolicy {
  type: 'size'
  unit: SizeUnit
  /** Strategy when size exceeded: oldest_first, largest_first, custom */
  strategy?: 'oldest_first' | 'largest_first' | 'custom'
}

/**
 * Count-based retention policy
 */
export interface CountRetentionPolicy extends RetentionPolicy {
  type: 'count'
  /** Order by clause for determining which records to keep */
  orderBy?: {
    column: string
    direction: 'asc' | 'desc'
  }
}

/**
 * Custom retention policy with predicate
 */
export interface CustomRetentionPolicy extends RetentionPolicy {
  type: 'custom'
  /** SQL predicate that returns true for records to delete */
  deletePredicate: string
  /** Optional SQL to run before deletion */
  preDeleteHook?: string
  /** Optional SQL to run after deletion */
  postDeleteHook?: string
}

/**
 * Legal hold configuration
 */
export interface LegalHold {
  /** Unique identifier */
  id: string
  /** Name of the legal hold */
  name: string
  /** Description and legal reference */
  description: string
  /** Tables/data affected */
  scope: LegalHoldScope[]
  /** When the hold was created */
  createdAt: Date
  /** Who created the hold */
  createdBy: string
  /** Expected release date (informational) */
  expectedReleaseDate?: Date | undefined
  /** Whether the hold is active */
  active: boolean
  /** Legal case reference */
  caseReference?: string | undefined
  /** Additional metadata */
  metadata?: Record<string, unknown> | undefined
}

/**
 * Scope definition for legal holds
 */
export interface LegalHoldScope {
  /** Table name */
  table: string
  /** Schema name */
  schema?: string
  /** Optional predicate to limit scope */
  predicate?: string
  /** Specific record IDs (if known) */
  recordIds?: string[]
}

/**
 * GDPR erasure request
 */
export interface GdprErasureRequest {
  /** Unique request identifier */
  id: string
  /** Subject identifier (user ID, email, etc.) */
  subjectId: string
  /** Type of identifier */
  subjectIdType: 'user_id' | 'email' | 'phone' | 'custom'
  /** When the request was received */
  requestedAt: Date
  /** Deadline for completion (72 hours from request per GDPR) */
  deadline: Date
  /** Current status */
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'blocked'
  /** Tables to scan for subject data */
  tables: GdprTableMapping[]
  /** Results of the erasure */
  result?: GdprErasureResult | undefined
  /** Reason if blocked (e.g., legal hold) */
  blockedReason?: string | undefined
  /** Audit trail */
  auditTrail: AuditEntry[]
}

/**
 * Table mapping for GDPR erasure
 */
export interface GdprTableMapping {
  /** Table name */
  table: string
  /** Schema name */
  schema?: string
  /** Column containing the subject identifier */
  subjectColumn: string
  /** Action: delete, anonymize, or pseudonymize */
  action: 'delete' | 'anonymize' | 'pseudonymize'
  /** Columns to anonymize (if action is anonymize) */
  anonymizeColumns?: string[]
  /** Pseudonymization mapping (if action is pseudonymize) */
  pseudonymizeMap?: Record<string, string>
}

/**
 * Result of a GDPR erasure operation
 */
export interface GdprErasureResult {
  /** When erasure completed */
  completedAt: Date
  /** Total records affected */
  totalRecords: number
  /** Records per table */
  recordsByTable: Record<string, number>
  /** Any errors encountered */
  errors: GdprErasureError[]
  /** Verification hash (proof of erasure) */
  verificationHash?: string
}

/**
 * Error during GDPR erasure
 */
export interface GdprErasureError {
  /** Table where error occurred */
  table: string
  /** Error message */
  message: string
  /** Whether the error was recoverable */
  recoverable: boolean
  /** Record IDs that failed (if known) */
  failedRecordIds?: string[]
}

/**
 * Audit entry for tracking deletions
 */
export interface AuditEntry {
  /** Timestamp of the action */
  timestamp: Date
  /** Action taken */
  action: string
  /** Actor (user or system) */
  actor: string
  /** Details of the action */
  details: Record<string, unknown>
  /** Affected table */
  table?: string
  /** Number of records affected */
  recordCount?: number
  /** Hash of deleted data (for compliance verification) */
  dataHash?: string
}

/**
 * Purge operation request
 */
export interface PurgeRequest {
  /** Policy ID triggering the purge */
  policyId?: string
  /** Target tables */
  targets: PurgeTarget[]
  /** Action type */
  action: PurgeAction
  /** Whether to run in dry-run mode */
  dryRun?: boolean
  /** Maximum records to purge in this operation */
  batchSize?: number
  /** Timeout for the operation in milliseconds */
  timeoutMs?: number
  /** Reason for the purge */
  reason?: string
  /** Actor initiating the purge */
  actor?: string
}

/**
 * Target for a purge operation
 */
export interface PurgeTarget {
  /** Table name */
  table: string
  /** Schema name */
  schema?: string
  /** WHERE clause predicate */
  predicate: string
  /** Order by clause (for batch processing) */
  orderBy?: string
}

/**
 * Result of a purge operation
 */
export interface PurgeResult {
  /** Whether the operation succeeded */
  success: boolean
  /** Request that was executed */
  request: PurgeRequest
  /** When the purge started */
  startedAt: Date
  /** When the purge completed */
  completedAt: Date
  /** Total records purged */
  totalPurged: number
  /** Records purged per table */
  purgedByTable: Record<string, number>
  /** Any errors encountered */
  errors: PurgeError[]
  /** Audit entries created */
  auditEntries: AuditEntry[]
  /** Whether this was a dry run */
  wasDryRun: boolean
}

/**
 * Error during purge operation
 */
export interface PurgeError {
  /** Table where error occurred */
  table: string
  /** Error message */
  message: string
  /** Error code (PostgreSQL error code if applicable) */
  code?: string | undefined
  /** Whether the operation was rolled back */
  rolledBack: boolean
}

/**
 * Configuration for the retention manager
 */
export interface RetentionManagerConfig {
  /** SQL client for executing queries */
  sql: RetentionSqlClient
  /** Table to store policies (default: _retention_policies) */
  policiesTable?: string
  /** Table to store audit entries (default: _retention_audit) */
  auditTable?: string
  /** Table to store legal holds (default: _legal_holds) */
  legalHoldsTable?: string
  /** Table to store GDPR requests (default: _gdpr_requests) */
  gdprRequestsTable?: string
  /** Schema for retention tables (default: postgres_do) */
  schema?: string
  /** Default batch size for purge operations */
  defaultBatchSize?: number
  /** Enable automatic scheduling */
  enableScheduler?: boolean
  /** Callback for audit events */
  onAuditEvent?: (entry: AuditEntry) => void | Promise<void>
}

/**
 * Minimal SQL client interface for retention operations
 */
export interface RetentionSqlClient {
  unsafe<T extends Record<string, unknown> = Record<string, unknown>>(
    query: string,
    params?: unknown[]
  ): Promise<T[]>
}

/**
 * Convert time value to milliseconds
 */
export function timeToMs(value: number, unit: TimeUnit): number {
  const multipliers: Record<TimeUnit, number> = {
    seconds: 1000,
    minutes: 60 * 1000,
    hours: 60 * 60 * 1000,
    days: 24 * 60 * 60 * 1000,
    weeks: 7 * 24 * 60 * 60 * 1000,
    months: 30 * 24 * 60 * 60 * 1000, // Approximate
    years: 365 * 24 * 60 * 60 * 1000, // Approximate
  }
  return value * multipliers[unit]
}

/**
 * Convert size value to bytes
 */
export function sizeToBytes(value: number, unit: SizeUnit): number {
  const multipliers: Record<SizeUnit, number> = {
    bytes: 1,
    kb: 1024,
    mb: 1024 * 1024,
    gb: 1024 * 1024 * 1024,
    tb: 1024 * 1024 * 1024 * 1024,
  }
  return value * multipliers[unit]
}

/**
 * Convert PostgreSQL interval string from time value and unit
 */
export function toPgInterval(value: number, unit: TimeUnit): string {
  const pgUnits: Record<TimeUnit, string> = {
    seconds: 'seconds',
    minutes: 'minutes',
    hours: 'hours',
    days: 'days',
    weeks: 'weeks',
    months: 'months',
    years: 'years',
  }
  return `${value} ${pgUnits[unit]}`
}

/**
 * GDPR deadline: 72 hours from request time
 */
const GDPR_DEADLINE_MS = 72 * 60 * 60 * 1000

/**
 * Generate a unique ID with a given prefix
 */
function generateId(prefix: string = 'ret'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/**
 * Retention Manager
 *
 * Main class for managing retention policies, legal holds, and GDPR requests.
 *
 * @example
 * ```typescript
 * import { RetentionManager } from 'postgres.do/retention'
 *
 * const retention = new RetentionManager({ sql })
 *
 * // Create a time-based retention policy
 * const policy = await retention.createPolicy({
 *   name: 'Delete old logs',
 *   type: 'time',
 *   value: 30,
 *   unit: 'days',
 *   targets: [{ table: 'logs', timestampColumn: 'created_at' }],
 *   action: 'hard_delete',
 * })
 *
 * // Execute the policy
 * const result = await retention.executePolicy(policy.id)
 * console.log(`Deleted ${result.totalPurged} records`)
 * ```
 */
export class RetentionManager {
  private config: Required<RetentionManagerConfig>
  private scheduler: RetentionScheduler | null = null

  constructor(config: RetentionManagerConfig) {
    this.config = {
      sql: config.sql,
      policiesTable: config.policiesTable ?? '_retention_policies',
      auditTable: config.auditTable ?? '_retention_audit',
      legalHoldsTable: config.legalHoldsTable ?? '_legal_holds',
      gdprRequestsTable: config.gdprRequestsTable ?? '_gdpr_requests',
      schema: config.schema ?? 'postgres_do',
      defaultBatchSize: config.defaultBatchSize ?? 1000,
      enableScheduler: config.enableScheduler ?? false,
      onAuditEvent: config.onAuditEvent ?? (async () => {}),
    }
  }

  /**
   * Initialize the retention system (creates required tables)
   */
  async initialize(): Promise<void> {
    const { sql, schema, policiesTable, auditTable, legalHoldsTable, gdprRequestsTable } = this.config

    // Create schema if it doesn't exist
    await sql.unsafe(`CREATE SCHEMA IF NOT EXISTS ${schema}`)

    // Create policies table
    await sql.unsafe(`
      CREATE TABLE IF NOT EXISTS ${schema}.${policiesTable} (
        id TEXT PRIMARY KEY,
        name TEXT NOT NULL,
        description TEXT,
        type TEXT NOT NULL,
        value NUMERIC NOT NULL,
        unit TEXT,
        targets JSONB NOT NULL,
        status TEXT NOT NULL DEFAULT 'active',
        priority INTEGER NOT NULL DEFAULT 100,
        compliance JSONB,
        schedule JSONB,
        action TEXT NOT NULL,
        cascade_policy JSONB,
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
      )
    `)

    // Create audit table
    await sql.unsafe(`
      CREATE TABLE IF NOT EXISTS ${schema}.${auditTable} (
        id SERIAL PRIMARY KEY,
        timestamp TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        action TEXT NOT NULL,
        actor TEXT NOT NULL,
        details JSONB NOT NULL,
        table_name TEXT,
        record_count INTEGER,
        data_hash TEXT,
        policy_id TEXT REFERENCES ${schema}.${policiesTable}(id),
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
      )
    `)

    // Create legal holds table
    await sql.unsafe(`
      CREATE TABLE IF NOT EXISTS ${schema}.${legalHoldsTable} (
        id TEXT PRIMARY KEY,
        name TEXT NOT NULL,
        description TEXT NOT NULL,
        scope JSONB NOT NULL,
        created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        created_by TEXT NOT NULL,
        expected_release_date TIMESTAMPTZ,
        active BOOLEAN NOT NULL DEFAULT TRUE,
        case_reference TEXT,
        metadata JSONB
      )
    `)

    // Create GDPR requests table
    await sql.unsafe(`
      CREATE TABLE IF NOT EXISTS ${schema}.${gdprRequestsTable} (
        id TEXT PRIMARY KEY,
        subject_id TEXT NOT NULL,
        subject_id_type TEXT NOT NULL,
        requested_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        deadline TIMESTAMPTZ NOT NULL,
        status TEXT NOT NULL DEFAULT 'pending',
        tables JSONB NOT NULL,
        result JSONB,
        blocked_reason TEXT,
        audit_trail JSONB NOT NULL DEFAULT '[]'::JSONB
      )
    `)

    // Create indexes for efficient queries
    await sql.unsafe(`
      CREATE INDEX IF NOT EXISTS idx_${policiesTable}_status
      ON ${schema}.${policiesTable}(status)
    `)
    await sql.unsafe(`
      CREATE INDEX IF NOT EXISTS idx_${auditTable}_timestamp
      ON ${schema}.${auditTable}(timestamp)
    `)
    await sql.unsafe(`
      CREATE INDEX IF NOT EXISTS idx_${legalHoldsTable}_active
      ON ${schema}.${legalHoldsTable}(active)
    `)
    await sql.unsafe(`
      CREATE INDEX IF NOT EXISTS idx_${gdprRequestsTable}_status
      ON ${schema}.${gdprRequestsTable}(status)
    `)
    await sql.unsafe(`
      CREATE INDEX IF NOT EXISTS idx_${gdprRequestsTable}_deadline
      ON ${schema}.${gdprRequestsTable}(deadline)
    `)
  }

  /**
   * Create a new retention policy
   */
  async createPolicy(
    options: Omit<RetentionPolicy, 'id' | 'createdAt' | 'updatedAt' | 'status'> & { status?: PolicyStatus }
  ): Promise<RetentionPolicy> {
    const { sql, schema, policiesTable } = this.config

    const policy: RetentionPolicy = {
      id: generateId(),
      name: options.name,
      type: options.type,
      value: options.value,
      targets: options.targets,
      status: options.status ?? 'active',
      priority: options.priority ?? 100,
      action: options.action,
      createdAt: new Date(),
      updatedAt: new Date(),
    }
    if (options.description !== undefined) {
      policy.description = options.description
    }
    if (options.unit !== undefined) {
      policy.unit = options.unit
    }
    if (options.compliance !== undefined) {
      policy.compliance = options.compliance
    }
    if (options.schedule !== undefined) {
      policy.schedule = options.schedule
    }
    if (options.cascade !== undefined) {
      policy.cascade = options.cascade
    }

    await sql.unsafe(
      `
      INSERT INTO ${schema}.${policiesTable}
      (id, name, description, type, value, unit, targets, status, priority, compliance, schedule, action, cascade_policy, created_at, updated_at)
      VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15)
    `,
      [
        policy.id,
        policy.name,
        policy.description ?? null,
        policy.type,
        policy.value,
        policy.unit ?? null,
        JSON.stringify(policy.targets),
        policy.status,
        policy.priority,
        policy.compliance ? JSON.stringify(policy.compliance) : null,
        policy.schedule ? JSON.stringify(policy.schedule) : null,
        policy.action,
        policy.cascade ? JSON.stringify(policy.cascade) : null,
        policy.createdAt.toISOString(),
        policy.updatedAt.toISOString(),
      ]
    )

    await this.audit({
      action: 'policy_created',
      actor: 'system',
      details: { policyId: policy.id, policyName: policy.name, policyType: policy.type },
    })

    return policy
  }

  /**
   * Get a policy by ID
   */
  async getPolicy(id: string): Promise<RetentionPolicy | null> {
    const { sql, schema, policiesTable } = this.config

    const rows = await sql.unsafe<Record<string, unknown>>(
      `SELECT * FROM ${schema}.${policiesTable} WHERE id = $1`,
      [id]
    )

    if (rows.length === 0) {
      return null
    }

    return this.rowToPolicy(rows[0]!)
  }

  /**
   * List all policies
   */
  async listPolicies(options?: {
    status?: PolicyStatus
    type?: RetentionType
    compliance?: ComplianceFramework
  }): Promise<RetentionPolicy[]> {
    const { sql, schema, policiesTable } = this.config

    let query = `SELECT * FROM ${schema}.${policiesTable} WHERE 1=1`
    const params: unknown[] = []
    let paramIndex = 1

    if (options?.status) {
      query += ` AND status = $${paramIndex++}`
      params.push(options.status)
    }
    if (options?.type) {
      query += ` AND type = $${paramIndex++}`
      params.push(options.type)
    }
    if (options?.compliance) {
      query += ` AND compliance ? $${paramIndex++}`
      params.push(options.compliance)
    }

    query += ' ORDER BY priority ASC, created_at DESC'

    const rows = await sql.unsafe<Record<string, unknown>>(query, params)
    return rows.map((row) => this.rowToPolicy(row))
  }

  /**
   * Update a policy
   */
  async updatePolicy(
    id: string,
    updates: Partial<Omit<RetentionPolicy, 'id' | 'createdAt'>>
  ): Promise<RetentionPolicy | null> {
    const { sql, schema, policiesTable } = this.config

    const existing = await this.getPolicy(id)
    if (!existing) {
      return null
    }

    const updated: RetentionPolicy = {
      ...existing,
      ...updates,
      updatedAt: new Date(),
    }

    await sql.unsafe(
      `
      UPDATE ${schema}.${policiesTable}
      SET name = $1, description = $2, type = $3, value = $4, unit = $5,
          targets = $6, status = $7, priority = $8, compliance = $9,
          schedule = $10, action = $11, cascade_policy = $12, updated_at = $13
      WHERE id = $14
    `,
      [
        updated.name,
        updated.description ?? null,
        updated.type,
        updated.value,
        updated.unit ?? null,
        JSON.stringify(updated.targets),
        updated.status,
        updated.priority,
        updated.compliance ? JSON.stringify(updated.compliance) : null,
        updated.schedule ? JSON.stringify(updated.schedule) : null,
        updated.action,
        updated.cascade ? JSON.stringify(updated.cascade) : null,
        updated.updatedAt.toISOString(),
        id,
      ]
    )

    await this.audit({
      action: 'policy_updated',
      actor: 'system',
      details: { policyId: id, updates: Object.keys(updates) },
    })

    return updated
  }

  /**
   * Delete a policy
   */
  async deletePolicy(id: string): Promise<boolean> {
    const { sql, schema, policiesTable } = this.config

    const result = await sql.unsafe(`DELETE FROM ${schema}.${policiesTable} WHERE id = $1 RETURNING id`, [id])

    if (result.length > 0) {
      await this.audit({
        action: 'policy_deleted',
        actor: 'system',
        details: { policyId: id },
      })
      return true
    }

    return false
  }

  /**
   * Execute a retention policy
   */
  async executePolicy(policyId: string, options?: { dryRun?: boolean; actor?: string }): Promise<PurgeResult> {
    const policy = await this.getPolicy(policyId)
    if (!policy) {
      throw new Error(`Policy not found: ${policyId}`)
    }

    if (policy.status !== 'active') {
      throw new Error(`Policy is not active: ${policyId}`)
    }

    // Check for legal holds
    const blockedTables = await this.getBlockedTables(policy.targets.map((t) => t.table))
    if (blockedTables.length > 0) {
      throw new Error(`Cannot execute policy: tables under legal hold: ${blockedTables.join(', ')}`)
    }

    // Build purge request based on policy type
    const purgeTargets = await this.buildPurgeTargets(policy)

    const request: PurgeRequest = {
      policyId,
      targets: purgeTargets,
      action: policy.action,
      dryRun: options?.dryRun ?? false,
      batchSize: this.config.defaultBatchSize,
      reason: `Retention policy: ${policy.name}`,
      actor: options?.actor ?? 'scheduler',
    }

    return this.purge(request)
  }

  /**
   * Build purge targets based on policy type
   */
  private async buildPurgeTargets(policy: RetentionPolicy): Promise<PurgeTarget[]> {
    const targets: PurgeTarget[] = []

    for (const target of policy.targets) {
      const schema = target.schema ?? 'public'
      let predicate: string

      switch (policy.type) {
        case 'time': {
          const timePolicy = policy as TimeRetentionPolicy
          const column = target.timestampColumn ?? 'created_at'
          const interval = toPgInterval(timePolicy.value, timePolicy.unit)
          predicate = `${column} < NOW() - INTERVAL '${interval}'`
          break
        }

        case 'count': {
          const countPolicy = policy as CountRetentionPolicy
          const orderColumn = countPolicy.orderBy?.column ?? 'id'
          const orderDir = countPolicy.orderBy?.direction ?? 'desc'
          // For count-based, we'll select IDs to keep and delete the rest
          predicate = `id NOT IN (
            SELECT id FROM ${schema}.${target.table}
            ORDER BY ${orderColumn} ${orderDir}
            LIMIT ${countPolicy.value}
          )`
          break
        }

        case 'size': {
          // Size-based requires calculating table size and removing oldest records
          const sizePolicy = policy as SizeRetentionPolicy
          const targetBytes = sizeToBytes(sizePolicy.value, sizePolicy.unit)
          const strategy = sizePolicy.strategy ?? 'oldest_first'
          const orderColumn = strategy === 'oldest_first' ? (target.timestampColumn ?? 'created_at') : 'pg_column_size(t.*)'
          const orderDir = strategy === 'oldest_first' ? 'ASC' : 'DESC'

          // Get current size and calculate how much to remove
          const sizeResult = await this.config.sql.unsafe<{ total_size: string }>(
            `SELECT pg_total_relation_size('${schema}.${target.table}') as total_size`
          )
          const currentSize = parseInt(sizeResult[0]?.total_size ?? '0', 10)

          if (currentSize <= targetBytes) {
            continue // Skip this target, already under size limit
          }

          // Estimate rows to delete (rough estimate based on size ratio)
          const countResult = await this.config.sql.unsafe<{ count: string }>(
            `SELECT COUNT(*) as count FROM ${schema}.${target.table}`
          )
          const totalRows = parseInt(countResult[0]?.count ?? '0', 10)
          const bytesPerRow = totalRows > 0 ? currentSize / totalRows : 0
          const bytesToRemove = currentSize - targetBytes
          const rowsToRemove = bytesPerRow > 0 ? Math.ceil(bytesToRemove / bytesPerRow) : 0

          predicate = `id IN (
            SELECT id FROM ${schema}.${target.table} t
            ORDER BY ${orderColumn} ${orderDir}
            LIMIT ${rowsToRemove}
          )`
          break
        }

        case 'custom': {
          const customPolicy = policy as CustomRetentionPolicy
          predicate = customPolicy.deletePredicate
          break
        }

        default:
          throw new Error(`Unknown policy type: ${policy.type}`)
      }

      // Add any additional predicate from target
      if (target.predicate) {
        predicate = `(${predicate}) AND (${target.predicate})`
      }

      targets.push({
        table: target.table,
        schema,
        predicate,
        orderBy: target.timestampColumn ?? 'id',
      })
    }

    return targets
  }

  /**
   * Execute a purge operation
   */
  async purge(request: PurgeRequest): Promise<PurgeResult> {
    const { sql } = this.config
    const startedAt = new Date()
    const errors: PurgeError[] = []
    const auditEntries: AuditEntry[] = []
    const purgedByTable: Record<string, number> = {}
    let totalPurged = 0

    for (const target of request.targets) {
      const schema = target.schema ?? 'public'
      const fullTable = `${schema}.${target.table}`

      try {
        // Check for legal holds
        const isBlocked = await this.isTableBlocked(target.table)
        if (isBlocked) {
          errors.push({
            table: fullTable,
            message: 'Table is under legal hold',
            rolledBack: false,
          })
          continue
        }

        let deletedCount = 0

        if (request.dryRun) {
          // Dry run: just count matching records
          const countResult = await sql.unsafe<{ count: string }>(
            `SELECT COUNT(*) as count FROM ${fullTable} WHERE ${target.predicate}`
          )
          deletedCount = parseInt(countResult[0]?.count ?? '0', 10)
        } else {
          // Execute based on action type
          switch (request.action) {
            case 'soft_delete': {
              // Soft delete: set deleted_at timestamp
              const result = await sql.unsafe<{ id: unknown }>(
                `UPDATE ${fullTable} SET deleted_at = NOW()
                 WHERE ${target.predicate} AND deleted_at IS NULL
                 RETURNING id`
              )
              deletedCount = result.length
              break
            }

            case 'hard_delete': {
              // Hard delete: actually remove records
              const result = await sql.unsafe<{ id: unknown }>(
                `DELETE FROM ${fullTable} WHERE ${target.predicate} RETURNING id`
              )
              deletedCount = result.length
              break
            }

            case 'archive': {
              // Archive: move to archive table
              const archiveTable = `${fullTable}_archive`
              await sql.unsafe(
                `INSERT INTO ${archiveTable}
                 SELECT *, NOW() as archived_at FROM ${fullTable}
                 WHERE ${target.predicate}`
              )
              const result = await sql.unsafe<{ id: unknown }>(
                `DELETE FROM ${fullTable} WHERE ${target.predicate} RETURNING id`
              )
              deletedCount = result.length
              break
            }

            case 'cryptographic_erasure': {
              // Cryptographic erasure: delete encryption keys
              // This requires integration with key management - for now we hard delete
              const result = await sql.unsafe<{ id: unknown }>(
                `DELETE FROM ${fullTable} WHERE ${target.predicate} RETURNING id`
              )
              deletedCount = result.length
              break
            }
          }
        }

        purgedByTable[fullTable] = deletedCount
        totalPurged += deletedCount

        // Create audit entry for this table
        const auditEntry: AuditEntry = {
          timestamp: new Date(),
          action: request.dryRun ? 'purge_dry_run' : `purge_${request.action}`,
          actor: request.actor ?? 'system',
          details: {
            policyId: request.policyId,
            reason: request.reason,
            predicate: target.predicate,
          },
          table: fullTable,
          recordCount: deletedCount,
        }
        auditEntries.push(auditEntry)

        if (!request.dryRun) {
          await this.audit(auditEntry)
        }
      } catch (error) {
        errors.push({
          table: fullTable,
          message: error instanceof Error ? error.message : String(error),
          code: (error as { code?: string }).code,
          rolledBack: true,
        })
      }
    }

    const result: PurgeResult = {
      success: errors.length === 0,
      request,
      startedAt,
      completedAt: new Date(),
      totalPurged,
      purgedByTable,
      errors,
      auditEntries,
      wasDryRun: request.dryRun ?? false,
    }

    // Audit the overall operation
    if (!request.dryRun) {
      await this.audit({
        timestamp: new Date(),
        action: 'purge_completed',
        actor: request.actor ?? 'system',
        details: {
          policyId: request.policyId,
          success: result.success,
          totalPurged,
          errorCount: errors.length,
          durationMs: result.completedAt.getTime() - result.startedAt.getTime(),
        },
      })
    }

    return result
  }

  /**
   * Create a legal hold
   */
  async createLegalHold(
    options: Omit<LegalHold, 'id' | 'createdAt' | 'active'> & { active?: boolean }
  ): Promise<LegalHold> {
    const { sql, schema, legalHoldsTable } = this.config

    const hold: LegalHold = {
      id: generateId('hold'),
      name: options.name,
      description: options.description,
      scope: options.scope,
      createdAt: new Date(),
      createdBy: options.createdBy,
      expectedReleaseDate: options.expectedReleaseDate,
      active: options.active ?? true,
      caseReference: options.caseReference,
      metadata: options.metadata,
    }

    await sql.unsafe(
      `
      INSERT INTO ${schema}.${legalHoldsTable}
      (id, name, description, scope, created_at, created_by, expected_release_date, active, case_reference, metadata)
      VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)
    `,
      [
        hold.id,
        hold.name,
        hold.description,
        JSON.stringify(hold.scope),
        hold.createdAt.toISOString(),
        hold.createdBy,
        hold.expectedReleaseDate?.toISOString() ?? null,
        hold.active,
        hold.caseReference ?? null,
        hold.metadata ? JSON.stringify(hold.metadata) : null,
      ]
    )

    await this.audit({
      action: 'legal_hold_created',
      actor: hold.createdBy,
      details: {
        holdId: hold.id,
        holdName: hold.name,
        scope: hold.scope.map((s) => s.table),
      },
    })

    return hold
  }

  /**
   * Release a legal hold
   */
  async releaseLegalHold(id: string, actor: string, reason?: string): Promise<boolean> {
    const { sql, schema, legalHoldsTable } = this.config

    const result = await sql.unsafe(
      `UPDATE ${schema}.${legalHoldsTable} SET active = FALSE WHERE id = $1 AND active = TRUE RETURNING id`,
      [id]
    )

    if (result.length > 0) {
      await this.audit({
        action: 'legal_hold_released',
        actor,
        details: { holdId: id, reason },
      })
      return true
    }

    return false
  }

  /**
   * Check if a table is under legal hold
   */
  async isTableBlocked(table: string): Promise<boolean> {
    const { sql, schema, legalHoldsTable } = this.config

    const result = await sql.unsafe<{ count: string }>(
      `
      SELECT COUNT(*) as count FROM ${schema}.${legalHoldsTable}
      WHERE active = TRUE AND scope @> $1::JSONB
    `,
      [JSON.stringify([{ table }])]
    )

    return parseInt(result[0]?.count ?? '0', 10) > 0
  }

  /**
   * Get all tables currently under legal hold
   */
  private async getBlockedTables(tables: string[]): Promise<string[]> {
    const blocked: string[] = []
    for (const table of tables) {
      if (await this.isTableBlocked(table)) {
        blocked.push(table)
      }
    }
    return blocked
  }

  /**
   * List all active legal holds
   */
  async listLegalHolds(options?: { active?: boolean }): Promise<LegalHold[]> {
    const { sql, schema, legalHoldsTable } = this.config

    let query = `SELECT * FROM ${schema}.${legalHoldsTable}`
    const params: unknown[] = []

    if (options?.active !== undefined) {
      query += ' WHERE active = $1'
      params.push(options.active)
    }

    query += ' ORDER BY created_at DESC'

    const rows = await sql.unsafe<Record<string, unknown>>(query, params)
    return rows.map((row) => this.rowToLegalHold(row))
  }

  /**
   * Create a GDPR erasure request
   */
  async createGdprErasureRequest(
    options: Omit<GdprErasureRequest, 'id' | 'requestedAt' | 'deadline' | 'status' | 'auditTrail'>
  ): Promise<GdprErasureRequest> {
    const { sql, schema, gdprRequestsTable } = this.config

    const requestedAt = new Date()
    const deadline = new Date(requestedAt.getTime() + GDPR_DEADLINE_MS)

    const request: GdprErasureRequest = {
      id: generateId('gdpr'),
      subjectId: options.subjectId,
      subjectIdType: options.subjectIdType,
      requestedAt,
      deadline,
      status: 'pending',
      tables: options.tables,
      auditTrail: [
        {
          timestamp: requestedAt,
          action: 'request_created',
          actor: 'system',
          details: { subjectId: options.subjectId, subjectIdType: options.subjectIdType },
        },
      ],
    }

    await sql.unsafe(
      `
      INSERT INTO ${schema}.${gdprRequestsTable}
      (id, subject_id, subject_id_type, requested_at, deadline, status, tables, audit_trail)
      VALUES ($1, $2, $3, $4, $5, $6, $7, $8)
    `,
      [
        request.id,
        request.subjectId,
        request.subjectIdType,
        request.requestedAt.toISOString(),
        request.deadline.toISOString(),
        request.status,
        JSON.stringify(request.tables),
        JSON.stringify(request.auditTrail),
      ]
    )

    await this.audit({
      action: 'gdpr_request_created',
      actor: 'system',
      details: {
        requestId: request.id,
        subjectId: request.subjectId,
        deadline: request.deadline.toISOString(),
      },
    })

    return request
  }

  /**
   * Execute a GDPR erasure request
   */
  async executeGdprErasureRequest(
    requestId: string,
    options?: { actor?: string }
  ): Promise<GdprErasureResult> {
    const { sql, schema, gdprRequestsTable } = this.config

    // Get the request
    const rows = await sql.unsafe<Record<string, unknown>>(
      `SELECT * FROM ${schema}.${gdprRequestsTable} WHERE id = $1`,
      [requestId]
    )

    if (rows.length === 0) {
      throw new Error(`GDPR request not found: ${requestId}`)
    }

    const request = this.rowToGdprRequest(rows[0]!)

    if (request.status === 'completed') {
      throw new Error('Request already completed')
    }

    if (request.status === 'blocked') {
      throw new Error(`Request is blocked: ${request.blockedReason}`)
    }

    // Check for legal holds
    const blockedTables = await this.getBlockedTables(request.tables.map((t) => t.table))
    if (blockedTables.length > 0) {
      // Update status to blocked
      await sql.unsafe(
        `UPDATE ${schema}.${gdprRequestsTable} SET status = 'blocked', blocked_reason = $1 WHERE id = $2`,
        [`Tables under legal hold: ${blockedTables.join(', ')}`, requestId]
      )
      throw new Error(`Cannot execute: tables under legal hold: ${blockedTables.join(', ')}`)
    }

    // Update status to in_progress
    await sql.unsafe(`UPDATE ${schema}.${gdprRequestsTable} SET status = 'in_progress' WHERE id = $1`, [
      requestId,
    ])

    const recordsByTable: Record<string, number> = {}
    const errors: GdprErasureError[] = []
    let totalRecords = 0

    for (const tableMapping of request.tables) {
      const tableSchema = tableMapping.schema ?? 'public'
      const fullTable = `${tableSchema}.${tableMapping.table}`

      try {
        let affected = 0

        switch (tableMapping.action) {
          case 'delete': {
            const result = await sql.unsafe<{ id: unknown }>(
              `DELETE FROM ${fullTable} WHERE ${tableMapping.subjectColumn} = $1 RETURNING id`,
              [request.subjectId]
            )
            affected = result.length
            break
          }

          case 'anonymize': {
            if (!tableMapping.anonymizeColumns || tableMapping.anonymizeColumns.length === 0) {
              throw new Error('anonymizeColumns required for anonymize action')
            }
            const setClause = tableMapping.anonymizeColumns.map((col) => `${col} = NULL`).join(', ')
            const result = await sql.unsafe<{ id: unknown }>(
              `UPDATE ${fullTable} SET ${setClause} WHERE ${tableMapping.subjectColumn} = $1 RETURNING id`,
              [request.subjectId]
            )
            affected = result.length
            break
          }

          case 'pseudonymize': {
            if (!tableMapping.pseudonymizeMap || Object.keys(tableMapping.pseudonymizeMap).length === 0) {
              throw new Error('pseudonymizeMap required for pseudonymize action')
            }
            const setClause = Object.entries(tableMapping.pseudonymizeMap)
              .map(([col, value]) => `${col} = '${value}'`)
              .join(', ')
            const result = await sql.unsafe<{ id: unknown }>(
              `UPDATE ${fullTable} SET ${setClause} WHERE ${tableMapping.subjectColumn} = $1 RETURNING id`,
              [request.subjectId]
            )
            affected = result.length
            break
          }
        }

        recordsByTable[fullTable] = affected
        totalRecords += affected
      } catch (error) {
        errors.push({
          table: fullTable,
          message: error instanceof Error ? error.message : String(error),
          recoverable: false,
        })
      }
    }

    const result: GdprErasureResult = {
      completedAt: new Date(),
      totalRecords,
      recordsByTable,
      errors,
    }

    // Update request with result
    const finalStatus = errors.length === 0 ? 'completed' : 'failed'
    const auditEntry: AuditEntry = {
      timestamp: new Date(),
      action: 'erasure_executed',
      actor: options?.actor ?? 'system',
      details: {
        totalRecords,
        errorCount: errors.length,
        tables: Object.keys(recordsByTable),
      },
    }

    request.auditTrail.push(auditEntry)

    await sql.unsafe(
      `UPDATE ${schema}.${gdprRequestsTable} SET status = $1, result = $2, audit_trail = $3 WHERE id = $4`,
      [finalStatus, JSON.stringify(result), JSON.stringify(request.auditTrail), requestId]
    )

    await this.audit({
      action: 'gdpr_erasure_completed',
      actor: options?.actor ?? 'system',
      details: {
        requestId,
        subjectId: request.subjectId,
        status: finalStatus,
        totalRecords,
        errorCount: errors.length,
      },
    })

    return result
  }

  /**
   * Get pending GDPR requests approaching deadline
   */
  async getPendingGdprRequests(options?: { withinHours?: number }): Promise<GdprErasureRequest[]> {
    const { sql, schema, gdprRequestsTable } = this.config

    const withinHours = options?.withinHours ?? 24
    const deadline = new Date(Date.now() + withinHours * 60 * 60 * 1000)

    const rows = await sql.unsafe<Record<string, unknown>>(
      `
      SELECT * FROM ${schema}.${gdprRequestsTable}
      WHERE status IN ('pending', 'in_progress')
        AND deadline <= $1
      ORDER BY deadline ASC
    `,
      [deadline.toISOString()]
    )

    return rows.map((row) => this.rowToGdprRequest(row))
  }

  /**
   * Create an audit entry
   */
  private async audit(entry: Omit<AuditEntry, 'timestamp'> & { timestamp?: Date }): Promise<void> {
    const { sql, schema, auditTable, onAuditEvent } = this.config

    const fullEntry: AuditEntry = {
      ...entry,
      timestamp: entry.timestamp ?? new Date(),
    }

    await sql.unsafe(
      `
      INSERT INTO ${schema}.${auditTable}
      (timestamp, action, actor, details, table_name, record_count, data_hash)
      VALUES ($1, $2, $3, $4, $5, $6, $7)
    `,
      [
        fullEntry.timestamp.toISOString(),
        fullEntry.action,
        fullEntry.actor,
        JSON.stringify(fullEntry.details),
        fullEntry.table ?? null,
        fullEntry.recordCount ?? null,
        fullEntry.dataHash ?? null,
      ]
    )

    // Call the audit event callback
    await onAuditEvent(fullEntry)
  }

  /**
   * Get audit trail for a policy or table
   */
  async getAuditTrail(options?: {
    policyId?: string
    table?: string
    since?: Date
    limit?: number
  }): Promise<AuditEntry[]> {
    const { sql, schema, auditTable } = this.config

    let query = `SELECT * FROM ${schema}.${auditTable} WHERE 1=1`
    const params: unknown[] = []
    let paramIndex = 1

    if (options?.policyId) {
      query += ` AND details->>'policyId' = $${paramIndex++}`
      params.push(options.policyId)
    }
    if (options?.table) {
      query += ` AND table_name = $${paramIndex++}`
      params.push(options.table)
    }
    if (options?.since) {
      query += ` AND timestamp >= $${paramIndex++}`
      params.push(options.since.toISOString())
    }

    query += ' ORDER BY timestamp DESC'

    if (options?.limit) {
      query += ` LIMIT ${options.limit}`
    }

    const rows = await sql.unsafe<Record<string, unknown>>(query, params)
    return rows.map((row) => {
      const entry: AuditEntry = {
        timestamp: new Date(row.timestamp as string),
        action: row.action as string,
        actor: row.actor as string,
        details: row.details as Record<string, unknown>,
      }
      if (row.table_name !== undefined && row.table_name !== null) {
        entry.table = row.table_name as string
      }
      if (row.record_count !== undefined && row.record_count !== null) {
        entry.recordCount = row.record_count as number
      }
      if (row.data_hash !== undefined && row.data_hash !== null) {
        entry.dataHash = row.data_hash as string
      }
      return entry
    })
  }

  /**
   * Start the scheduler for automatic policy execution
   */
  startScheduler(): void {
    if (this.scheduler) {
      return
    }
    this.scheduler = new RetentionScheduler(this)
    this.scheduler.start()
  }

  /**
   * Stop the scheduler
   */
  stopScheduler(): void {
    if (this.scheduler) {
      this.scheduler.stop()
      this.scheduler = null
    }
  }

  /**
   * Safely parse JSON that may already be an object (from JSONB columns)
   */
  private parseJsonField<T>(value: unknown): T {
    if (typeof value === 'string') {
      return JSON.parse(value) as T
    }
    return value as T
  }

  /**
   * Convert database row to RetentionPolicy
   */
  private rowToPolicy(row: Record<string, unknown>): RetentionPolicy {
    const policy: RetentionPolicy = {
      id: row.id as string,
      name: row.name as string,
      type: row.type as RetentionType,
      value: Number(row.value),
      targets: this.parseJsonField<RetentionTarget[]>(row.targets),
      status: row.status as PolicyStatus,
      priority: row.priority as number,
      action: row.action as PurgeAction,
      createdAt: new Date(row.created_at as string),
      updatedAt: new Date(row.updated_at as string),
    }

    // Add optional fields only if present
    if (row.description !== undefined && row.description !== null) {
      policy.description = row.description as string
    }
    if (row.unit !== undefined && row.unit !== null) {
      policy.unit = row.unit as TimeUnit | SizeUnit
    }
    if (row.compliance) {
      policy.compliance = this.parseJsonField<ComplianceFramework[]>(row.compliance)
    }
    if (row.schedule) {
      policy.schedule = this.parseJsonField<RetentionSchedule>(row.schedule)
    }
    if (row.cascade_policy) {
      policy.cascade = this.parseJsonField<CascadePolicy>(row.cascade_policy)
    }

    // Add custom policy fields if present
    if (row.type === 'custom') {
      const customPolicy = policy as CustomRetentionPolicy
      if (row.delete_predicate) {
        customPolicy.deletePredicate = row.delete_predicate as string
      }
      if (row.pre_delete_hook) {
        customPolicy.preDeleteHook = row.pre_delete_hook as string
      }
      if (row.post_delete_hook) {
        customPolicy.postDeleteHook = row.post_delete_hook as string
      }
    }

    return policy
  }

  /**
   * Convert database row to LegalHold
   */
  private rowToLegalHold(row: Record<string, unknown>): LegalHold {
    return {
      id: row.id as string,
      name: row.name as string,
      description: row.description as string,
      scope: this.parseJsonField<LegalHoldScope[]>(row.scope),
      createdAt: new Date(row.created_at as string),
      createdBy: row.created_by as string,
      expectedReleaseDate: row.expected_release_date
        ? new Date(row.expected_release_date as string)
        : undefined,
      active: row.active as boolean,
      caseReference: row.case_reference as string | undefined,
      metadata: row.metadata ? this.parseJsonField<Record<string, unknown>>(row.metadata) : undefined,
    }
  }

  /**
   * Convert database row to GdprErasureRequest
   */
  private rowToGdprRequest(row: Record<string, unknown>): GdprErasureRequest {
    return {
      id: row.id as string,
      subjectId: row.subject_id as string,
      subjectIdType: row.subject_id_type as 'user_id' | 'email' | 'phone' | 'custom',
      requestedAt: new Date(row.requested_at as string),
      deadline: new Date(row.deadline as string),
      status: row.status as GdprErasureRequest['status'],
      tables: this.parseJsonField<GdprTableMapping[]>(row.tables),
      result: row.result ? this.parseJsonField<GdprErasureResult>(row.result) : undefined,
      blockedReason: row.blocked_reason as string | undefined,
      auditTrail: this.parseJsonField<AuditEntry[]>(row.audit_trail),
    }
  }
}

/**
 * Internal scheduler for automatic policy execution
 */
class RetentionScheduler {
  private manager: RetentionManager
  private intervalIds: Map<string, ReturnType<typeof setInterval>> = new Map()
  private running = false

  constructor(manager: RetentionManager) {
    this.manager = manager
  }

  async start(): Promise<void> {
    if (this.running) return
    this.running = true

    // Get all active policies with schedules
    const policies = await this.manager.listPolicies({ status: 'active' })

    for (const policy of policies) {
      if (policy.schedule?.intervalMs) {
        const intervalId = setInterval(async () => {
          try {
            await this.manager.executePolicy(policy.id, { actor: 'scheduler' })
          } catch {
            // Log error but don't stop the scheduler
            console.error(`Failed to execute policy ${policy.id}`)
          }
        }, policy.schedule.intervalMs)

        this.intervalIds.set(policy.id, intervalId)

        // Run immediately if configured
        if (policy.schedule.runOnActivation) {
          try {
            await this.manager.executePolicy(policy.id, { actor: 'scheduler' })
          } catch {
            console.error(`Failed to execute policy ${policy.id} on activation`)
          }
        }
      }
    }
  }

  stop(): void {
    this.running = false
    for (const intervalId of Array.from(this.intervalIds.values())) {
      clearInterval(intervalId)
    }
    this.intervalIds.clear()
  }
}

/**
 * Compliance policy templates
 */
export const ComplianceTemplates = {
  /**
   * GDPR-compliant retention policy template
   */
  gdpr: {
    type: 'time' as const,
    value: 365,
    unit: 'days' as const,
    compliance: ['gdpr' as const],
    action: 'hard_delete' as const,
    description: 'GDPR-compliant data retention (1 year)',
  },

  /**
   * HIPAA-compliant retention policy template (6 years)
   */
  hipaa: {
    type: 'time' as const,
    value: 6,
    unit: 'years' as const,
    compliance: ['hipaa' as const],
    action: 'archive' as const,
    description: 'HIPAA-compliant medical record retention (6 years)',
  },

  /**
   * SOX-compliant retention policy template (7 years)
   */
  sox: {
    type: 'time' as const,
    value: 7,
    unit: 'years' as const,
    compliance: ['sox' as const],
    action: 'archive' as const,
    description: 'SOX-compliant financial record retention (7 years)',
  },

  /**
   * PCI-DSS compliant policy (1 year for logs)
   */
  pciDss: {
    type: 'time' as const,
    value: 1,
    unit: 'years' as const,
    compliance: ['pci_dss' as const],
    action: 'hard_delete' as const,
    description: 'PCI-DSS compliant audit log retention (1 year)',
  },

  /**
   * CCPA-compliant policy (varies by data type)
   */
  ccpa: {
    type: 'time' as const,
    value: 365,
    unit: 'days' as const,
    compliance: ['ccpa' as const],
    action: 'hard_delete' as const,
    description: 'CCPA-compliant personal data retention',
  },
} as const

/**
 * Create a pre-configured retention manager with compliance templates
 */
export function createRetentionManager(config: RetentionManagerConfig): RetentionManager {
  return new RetentionManager(config)
}
